import { createSlice } from "@reduxjs/toolkit";
import {
  loginBuilder,
  registerBuilder,
  logoutBuilder,
  verifyEmailBuilder,
  resendVerificationBuilder,
  forgotPasswordBuilder,
  resetPasswordBuilder,
  refreshBuilder,
  getUserInfoBuilder,
  updateProfileBuilder,
  changePasswordBuilder,
  getUserStatisticsBuilder,
  getUserAchievementsBuilder,
  getUserDashboardBuilder,
} from "./builder";
import { LOGOUT } from "../../../utils/constants";

const initialState = {
  user: null,
  loggedIn: null,
  error: null,
  authChecking: true,
  
  // User data
  statistics: null,
  achievements: null,
  dashboard: null,
  
  // Loading states
  loginPending: false,
  registerPending: false,
  verifyEmailPending: false,
  resendPending: false,
  forgotPasswordPending: false,
  resetPasswordPending: false,
  changePasswordPending: false,
  userInfoPending: false,
  statisticsPending: false,
  achievementsPending: false,
  dashboardPending: false,
  
  // Status flags
  needsEmailVerification: false,
  passwordResetRequested: false,
  passwordResetSuccess: false,
};

export const authSlice = createSlice({
  name: "authInfo",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPasswordResetStatus: (state) => {
      state.passwordResetRequested = false;
      state.passwordResetSuccess = false;
    },
    setLoggedIn: (state, { payload }) => {
      state.loggedIn = payload.loggedIn;
    },
  },
  extraReducers: (builder) => {
    loginBuilder(builder);
    registerBuilder(builder);
    logoutBuilder(builder);
    verifyEmailBuilder(builder);
    resendVerificationBuilder(builder);
    forgotPasswordBuilder(builder);
    resetPasswordBuilder(builder);
    refreshBuilder(builder);
    getUserInfoBuilder(builder);
    updateProfileBuilder(builder);
    changePasswordBuilder(builder);
    getUserStatisticsBuilder(builder);
    getUserAchievementsBuilder(builder);
    getUserDashboardBuilder(builder);

    builder.addCase(LOGOUT, () => ({ ...initialState, loggedIn: false }));
  },
});

export const { clearError, clearPasswordResetStatus, setLoggedIn } = authSlice.actions;

// User selectors
export const selectUser = (state) => state.authInfo.user;
export const selectUserRole = (state) => state.authInfo.user?.role;
export const selectLoggedIn = (state) => state.authInfo.loggedIn;
export const selectError = (state) => state.authInfo.error;
export const selectAuthChecking = (state) => state.authInfo.authChecking;

// Data selectors
export const selectStatistics = (state) => state.authInfo.statistics;
export const selectAchievements = (state) => state.authInfo.achievements;
export const selectDashboard = (state) => state.authInfo.dashboard;

// Loading selectors
export const selectLoginPending = (state) => state.authInfo.loginPending;
export const selectRegisterPending = (state) => state.authInfo.registerPending;
export const selectVerifyEmailPending = (state) => state.authInfo.verifyEmailPending;
export const selectResendPending = (state) => state.authInfo.resendPending;
export const selectForgotPasswordPending = (state) => state.authInfo.forgotPasswordPending;
export const selectResetPasswordPending = (state) => state.authInfo.resetPasswordPending;
export const selectUserInfoPending = (state) => state.authInfo.userInfoPending;
export const selectChangePasswordPending = (state) => state.authInfo.changePasswordPending;
export const selectStatisticsPending = (state) => state.authInfo.statisticsPending;
export const selectAchievementsPending = (state) => state.authInfo.achievementsPending;
export const selectDashboardPending = (state) => state.authInfo.dashboardPending;

// Status selectors
export const selectNeedsEmailVerification = (state) => state.authInfo.needsEmailVerification;
export const selectPasswordResetRequested = (state) => state.authInfo.passwordResetRequested;
export const selectPasswordResetSuccess = (state) => state.authInfo.passwordResetSuccess;

export default authSlice.reducer;
